/**
 * ParentMessenger - Handles all communication from iframe to parent window
 * 
 * This class provides a centralized, type-safe way to send messages from
 * the marketing embed iframe to its parent window.
 */

// Define message types that are actually handled by the parent
export const MessageTypes = {
  JOURNEY_BUILDER_READY: 'JOURNEY_BUILDER_READY',
  JOURNEY_GENERATED: 'JOURNEY_GENERATED',
  JOURNEY_RESET: 'JOURNEY_RESET',
  TAB_SYSTEM_READY: 'TAB_SYSTEM_READY',
  TAB_CHANGED: 'TAB_CHANGED',
  CONTENT_TYPE_CHANGE: 'CONTENT_TYPE_CHANGE',
  FULLSCREEN_REQUEST: 'FULLSCREEN_REQUEST',
  FALLBACK_SHOWN: 'FALLBACK_SHOWN'
} as const;

export type MessageType = typeof MessageTypes[keyof typeof MessageTypes];

// Define data shapes for each message type that the parent handles
interface MessageDataMap {
  [MessageTypes.JOURNEY_BUILDER_READY]: Record<string, never>;  // Empty data
  [MessageTypes.JOURNEY_GENERATED]: {
    journeyId: string;
  };
  [MessageTypes.JOURNEY_RESET]: Record<string, never>;  // Empty data
  [MessageTypes.TAB_SYSTEM_READY]: {
    tabs: Array<{ id: string; title: string }>;
    currentIndex: number;
  };
  [MessageTypes.TAB_CHANGED]: {
    tabIndex: number;  // Just the index, not the tab details
  };
  [MessageTypes.CONTENT_TYPE_CHANGE]: {
    contentType: 'form' | 'tabbed';
    hasNavigation: boolean;
  };
  [MessageTypes.FULLSCREEN_REQUEST]: {
    action: 'enter' | 'exit';
  };
  [MessageTypes.FALLBACK_SHOWN]: Record<string, never>;  // Empty data
}

class ParentMessenger {
  private static instance: ParentMessenger;
  private targetOrigin: string = '*';
  private isInitialized: boolean = false;

  private constructor() {
    this.initialize();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ParentMessenger {
    if (!ParentMessenger.instance) {
      ParentMessenger.instance = new ParentMessenger();
    }
    return ParentMessenger.instance;
  }

  /**
   * Initialize the messenger with proper target origin
   */
  private initialize(): void {
    if (this.isInitialized) return;

    // Only send to parent if we're in an iframe
    if (window.parent === window) {
      console.warn('ParentMessenger: Not in an iframe, messages will not be sent');
      return;
    }

    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';

    if (!isDevelopment) {
      // Try to get the parent's origin from the referrer
      if (document.referrer) {
        try {
          const referrerUrl = new URL(document.referrer);
          this.targetOrigin = referrerUrl.origin;
        } catch (e) {
          console.warn('Could not parse referrer URL, using wildcard origin');
        }
      } else {
        // Default to main domain if referrer is not available
        this.targetOrigin = 'https://www.blustream.io';
      }
    }

    this.isInitialized = true;
  }

  /**
   * Send a message to the parent window
   */
  send<T extends MessageType>(type: T, data: MessageDataMap[T]): void {
    // Don't send if not in iframe
    if (window.parent === window) {
      return;
    }

    // Validate domain if DomainValidator is available
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';

    if (!isDevelopment && !(window as any).DomainValidator) {
      console.error('Domain validator not loaded. Security cannot be guaranteed.');
      return;
    }

    try {
      const message = {
        type,
        ...data
      };

      window.parent.postMessage(message, this.targetOrigin);
      console.log(`ParentMessenger: Sent ${type}`, data);
    } catch (error) {
      console.error(`ParentMessenger: Failed to send ${type}`, error);
    }
  }

  /**
   * Static convenience method for sending messages
   */
  static send<T extends MessageType>(type: T, data: MessageDataMap[T]): void {
    ParentMessenger.getInstance().send(type, data);
  }
}

// Export singleton instance and static methods
export default ParentMessenger;

// Make it available globally for non-module scripts
declare global {
  interface Window {
    ParentMessenger: typeof ParentMessenger;
    MessageTypes: typeof MessageTypes;
  }
}

// Initialize on load
if (typeof window !== 'undefined') {
  window.ParentMessenger = ParentMessenger;
  window.MessageTypes = MessageTypes;
}